import { useState, useEffect } from 'react';
import { RefreshCw, TrendingUp, Users, BarChart3, MapPin } from 'lucide-react';
import apiServices from '../../services/api';
import type { RepresentativeStats } from '../../types/api';

import { ScoreTrendChart } from '../charts/ScoreTrendChart';

interface RepresentativeDashboardProps {
  representativeId: string;
}

export const RepresentativeDashboard: React.FC<
  RepresentativeDashboardProps
> = ({ representativeId }) => {
  const [stats, setStats] = useState<RepresentativeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiServices.admin.getRepresentativeStats(
        representativeId
      );
      setStats(data);
    } catch (err) {
      console.error('Error loading representative stats:', err);
      setError('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (representativeId) {
      loadStats();
    }
  }, [representativeId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
        {error || 'No se pudieron cargar las estadísticas'}
      </div>
    );
  }

  const { overview, scoreTrend, storePerformance } = stats;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            Mi Dashboard
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Tu rendimiento y estadísticas personales
          </p>
        </div>
        <button
          onClick={loadStats}
          disabled={loading}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm sm:text-base"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <BarChart3 className="w-8 h-8 text-green-600 mb-2" />
          <div className="text-3xl font-bold text-gray-900">
            {overview.totalReports}
          </div>
          <div className="text-sm text-gray-600">Mis Reportes</div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <TrendingUp className="w-8 h-8 text-blue-600 mb-2" />
          <div className="text-3xl font-bold text-gray-900">
            {overview.avgScore.toFixed(1)}%
          </div>
          <div className="text-sm text-gray-600">Mi Promedio</div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <Users className="w-8 h-8 text-purple-600 mb-2" />
          <div className="text-3xl font-bold text-gray-900">
            {overview.reportsThisMonth}
          </div>
          <div className="text-sm text-gray-600">Este Mes</div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <MapPin className="w-8 h-8 text-orange-600 mb-2" />
          <div className="text-3xl font-bold text-gray-900">
            {overview.storesAssigned}
          </div>
          <div className="text-sm text-gray-600">Tiendas Asignadas</div>
        </div>
      </div>

      {/* Score Trend */}
      <ScoreTrendChart data={scoreTrend} />

      {/* Store Performance Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            Rendimiento por Tienda
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700 text-xs sm:text-sm">
                  Tienda
                </th>
                <th className="text-center py-3 px-4 font-medium text-gray-700 text-xs sm:text-sm">
                  Promedio
                </th>
                <th className="text-center py-3 px-4 font-medium text-gray-700 text-xs sm:text-sm">
                  Reportes
                </th>
                <th className="text-center py-3 px-4 font-medium text-gray-700 text-xs sm:text-sm">
                  Última Visita
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {storePerformance
                .sort((a, b) => b.avgScore - a.avgScore)
                .map((store) => (
                  <tr key={store.storeId} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-900 font-medium text-xs sm:text-sm">
                      {store.storeName}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                          store.avgScore >= 80
                            ? 'bg-green-100 text-green-800'
                            : store.avgScore >= 50
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {store.avgScore.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center text-gray-600">
                      {store.totalReports}
                    </td>
                    <td className="py-3 px-4 text-center text-gray-600">
                      {new Date(store.lastVisit).toLocaleDateString('es-ES')}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
